import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppRoot, LoginOkRoute } from '@/app/AppRoot';
import { AppLayout } from '@/app/layouts/AppLayout';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { CalendarPage } from '@/features/calendar/pages/CalendarPage';
import { PatientsListPage } from '@/features/patients/pages/PatientsListPage';
import { PatientProfilePage } from '@/features/patients/pages/PatientProfilePage';
import { PatientTabRedirect } from '@/features/patients/pages/PatientTabRedirect';
import { LiveCheckPage } from '@/features/live-check/pages/LiveCheckPage';
import { LiveCheckResultPage } from '@/features/live-check/pages/LiveCheckResultPage';
import { InsuranceArPage } from '@/features/insurance-ar/pages/InsuranceArPage';
import { ArRedirect } from '@/features/insurance-ar/pages/ArRedirect';
import { ClaimsReviewPage } from '@/features/claims-review/pages/ClaimsReviewPage';
import { ClaimsAllPage } from '@/features/claims-review/pages/ClaimsAllPage';
import { ClaimRecordPage } from '@/features/claims-review/pages/ClaimRecordPage';
import { AppealsPage } from '@/features/appeals/pages/AppealsPage';
import { PacketPage } from '@/features/appeals/pages/PacketPage';
import { PostingPage } from '@/features/posting/pages/PostingPage';
import { ElvaWorkPage } from '@/features/elva-work/pages/ElvaWorkPage';
import { PrintPage as ElvaWorkPrintPage } from '@/features/elva-work/pages/PrintPage';
import { PreAuthsPage } from '@/features/preauths/pages/PreAuthsPage';
import { SettingsPage } from '@/features/settings/pages/SettingsPage';
import { HumanInTheLoopPage } from '@/features/human-in-the-loop/pages/HumanInTheLoopPage';
import { DocumentCenterPage } from '@/features/document-center/pages/DocumentCenterPage';
import { EligibilityReportPage } from '@/features/eligibility-report/pages/EligibilityReportPage';
import { NotFoundPage } from '@/features/shared/pages/NotFoundPage';
import { RouteErrorPage } from '@/features/shared/pages/RouteErrorPage';

export const router = createBrowserRouter([
  {
    element: <AppRoot />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: '/loginok',
        element: <LoginOkRoute />,
      },
      {
        // Print preview — full screen, no sidebar or app chrome (§7).
        path: 'denied/:claimId/packet',
        element: <PacketPage />,
      },
      {
        // Printed eligibility report (rcm-28, D170) — rendered sheet, no app chrome.
        path: 'patients/:patientId/report',
        element: <EligibilityReportPage />,
      },
      {
        // ELVA's work — printable report, no app chrome.
        path: 'elva-work/report/print',
        element: <ElvaWorkPrintPage />,
      },
      {
        path: '/',
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: <DashboardPage />,
          },
          {
            path: 'calendar',
            element: <CalendarPage />,
          },
          {
            path: 'patients',
            element: <PatientsListPage />,
          },
          {
            path: 'patients/:patientId',
            element: <PatientProfilePage />,
          },
          {
            // Legacy /patients/:id/profile and /insurance now fold into the
            // single insurance page.
            path: 'patients/:patientId/:tab',
            element: <PatientTabRedirect />,
          },
          {
            path: 'human-in-the-loop',
            element: <HumanInTheLoopPage />,
          },
          {
            path: 'human-in-the-loop/:id',
            element: <HumanInTheLoopPage />,
          },
          {
            path: 'worklist',
            element: <Navigate to="/human-in-the-loop" replace />,
          },
          {
            path: 'claims',
            element: <ClaimsReviewPage />,
          },
          {
            // All claims — the searchable, paged claim record (history, §3).
            path: 'claims/all',
            element: <ClaimsAllPage />,
          },
          {
            // A single permanent claim record (read-only, §4).
            path: 'claims/record/:id',
            element: <ClaimRecordPage />,
          },
          {
            path: 'claims/:claimId',
            element: <ClaimsReviewPage />,
          },
          {
            // No more /sent-claims; every claim lives in the claim record (D149).
            path: 'sent-claims',
            element: <Navigate to="/claims/all?status=sent" replace />,
          },
          {
            path: 'denied',
            element: <AppealsPage view="denied" />,
          },
          {
            path: 'denied/underpaid',
            element: <AppealsPage view="underpaid" />,
          },
          {
            path: 'denied/appeals',
            element: <AppealsPage view="appeals" />,
          },
          {
            // One appeal sent, open in place (D202).
            path: 'denied/appeals/:appealId',
            element: <AppealsPage view="appeals" />,
          },
          {
            path: 'denied/closed',
            element: <AppealsPage view="closed" />,
          },
          {
            path: 'denied/:claimId',
            element: <AppealsPage />,
          },
          {
            path: 'pre-auths',
            element: <PreAuthsPage view="to_send" />,
          },
          {
            path: 'pre-auths/sent',
            element: <PreAuthsPage view="sent" />,
          },
          {
            path: 'pre-auths/approved',
            element: <PreAuthsPage view="approved" />,
          },
          {
            path: 'pre-auths/:id',
            element: <PreAuthsPage />,
          },
          {
            path: 'posting',
            element: <PostingPage view="to_post" />,
          },
          {
            path: 'posting/posted',
            element: <PostingPage view="posted" />,
          },
          {
            path: 'posting/posted/:id',
            element: <PostingPage view="posted" />,
          },
          {
            path: 'elva-work',
            element: <ElvaWorkPage view="report" />,
          },
          {
            path: 'elva-work/log',
            element: <ElvaWorkPage view="log" />,
          },
          {
            path: 'elva-work/log/:id',
            element: <ElvaWorkPage view="log" />,
          },
          {
            path: 'posting/bank',
            element: <PostingPage view="bank" />,
          },
          {
            path: 'posting/bank/:id',
            element: <PostingPage view="bank" />,
          },
          {
            path: 'posting/matched',
            element: <PostingPage view="matched" />,
          },
          {
            path: 'posting/matched/:id',
            element: <PostingPage view="matched" />,
          },
          {
            path: 'posting/:id',
            element: <PostingPage view="to_post" />,
          },
          {
            path: 'insurance-ar',
            element: <ArRedirect />,
          },
          {
            path: 'insurance-ar/*',
            element: <ArRedirect />,
          },
          {
            path: 'ar',
            element: <InsuranceArPage />,
          },
          {
            path: 'ar/written-off',
            element: <InsuranceArPage view="written-off" />,
          },
          {
            path: 'ar/:claimId',
            element: <InsuranceArPage />,
          },
          {
            path: 'document-center',
            element: <DocumentCenterPage />,
          },
          {
            path: 'document-center/cards',
            element: <DocumentCenterPage tab="cards" />,
          },
          {
            path: 'document-center/cards/:cardId',
            element: <DocumentCenterPage tab="cards" />,
          },
          {
            path: 'document-center/checks',
            element: <DocumentCenterPage tab="checks" />,
          },
          {
            path: 'document-center/filed',
            element: <DocumentCenterPage tab="filed" />,
          },
          {
            path: 'document-center/filed/:id',
            element: <DocumentCenterPage tab="filed" />,
          },
          {
            path: 'document-center/:id',
            element: <DocumentCenterPage />,
          },
          {
            // Practice Baseline is out of V1 (D146); keep the path redirecting.
            path: 'practice-baseline',
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'live-check',
            element: <LiveCheckPage />,
          },
          {
            path: 'live-check/:checkId',
            element: <LiveCheckResultPage />,
          },
          {
            path: 'settings',
            element: <Navigate to="/settings/autopilot" replace />,
          },
          {
            path: 'settings/payers/:payerId',
            element: <SettingsPage />,
          },
          {
            path: 'settings/:page',
            element: <SettingsPage />,
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);
